import type { Category, CatalogItem, ContentKind } from '@viva/core';
import type { CatalogApi } from '@viva/data';
import { isUhdName } from './components/player/uhd-fallback';
import { currentRelay } from './network';

/**
 * 4K and 8K movies and series on devices that can't play them (#301).
 *
 * Those devices (asked once, `devicePlays`) don't see 4K/8K categories, search hits or "Recently
 * added" titles, a 4K/8K title opened from elsewhere plays its HD version or explains why it can't,
 * and the Movies and Series pages say to use another device for the full catalogue. Devices that play 4K get it either way. Live channels aren't touched.
 *
 * "Stop 4K through the relay" (off by default) spares the relay's upload when it runs full: devices
 * that stream through the relay (browser, Android, Android TV) are treated the same way whatever they
 * can play, with a note that 4K is paused. The LG and Samsung apps reach the provider themselves and keep 4K.
 */

let relayCut = false;

/** From the account session: the admin's "Stop 4K through the relay" setting. */
export function setUhdRelayCut(on: boolean): void {
  relayCut = on;
}

/** Whether 4K is stopped for this device because it streams through the relay. */
export function uhdCutByRelay(): boolean {
  return relayCut && currentRelay() !== null;
}

/** Why 4K/8K is held back here: the relay is spared ('relay'), or the device can't play it ('device'). */
export function uhdReason(): 'relay' | 'device' {
  return uhdCutByRelay() ? 'relay' : 'device';
}

/** Whether titles at this level are held back on this device. */
function levelBlocked(level: UhdLevel): boolean {
  if (uhdCutByRelay()) return true;
  return !devicePlays(level);
}

/** HEVC at level 5.1 (4K, Main 10 then Main) and 6.1 (8K). */
const CODECS = {
  '4k': ['hvc1.2.4.L153.B0', 'hvc1.1.6.L153.B0'],
  '8k': ['hvc1.2.4.L183.B0', 'hvc1.1.6.L183.B0'],
} as const;

export type UhdLevel = keyof typeof CODECS;

const known = new Map<UhdLevel, boolean>();

/**
 * Whether this device decodes 4K (or 8K) HEVC, asked of the browser's MediaSource and of its video
 * element. The desktop app plays files its own way and always counts. Firefox claims HEVC on some
 * systems and then shows nothing, so it never counts.
 */
export function devicePlays(level: UhdLevel): boolean {
  const cached = known.get(level);
  if (cached !== undefined) return cached;
  const answer = askDevice(level);
  known.set(level, answer);
  return answer;
}

function askDevice(level: UhdLevel): boolean {
  if (typeof location !== 'undefined' && location.protocol === 'app:') return true;
  if (typeof navigator === 'undefined' || /firefox/i.test(navigator.userAgent)) return false;
  const types = CODECS[level].map((codec) => `video/mp4; codecs="${codec}"`);
  try {
    if (typeof MediaSource !== 'undefined' && types.some((type) => MediaSource.isTypeSupported(type))) return true;
  } catch {
    // Old engines throw on unknown codec strings.
  }
  if (typeof document === 'undefined') return false;
  try {
    const video = document.createElement('video');
    return types.some((type) => video.canPlayType(type) === 'probably');
  } catch {
    return false;
  }
}

/** Forgets the device's answers (tests). */
export function forgetDeviceAnswers(): void {
  known.clear();
}

/** Whether a name marks an 8K version ("8K", "4320p"). */
export function isEightKName(name: string): boolean {
  return /(^|[^a-z0-9])(8k|4320p)([^a-z0-9]|$)/i.test(name);
}

/** The level a name asks for: '8k', '4k' or null (HD and below). */
export function uhdLevelOf(name: string): UhdLevel | null {
  if (isEightKName(name)) return '8k';
  return isUhdName(name) ? '4k' : null;
}

/** Whether 4K/8K titles are held back on this device: 4K stopped on the relay, or the device can't play 4K. */
export function uhdHidden(): boolean {
  return levelBlocked('4k');
}

/**
 * Whether this movie, series or episode is held back on this device. `seriesName`: an episode's
 * series, whose name often carries the mark ("4K - Lupin (2021)").
 */
export function blockedOnDevice(item: Pick<CatalogItem, 'name' | 'kind'>, seriesName?: string): boolean {
  if (item.kind === 'live') return false;
  const level = uhdLevelOf(item.name) ?? (seriesName ? uhdLevelOf(seriesName) : null);
  return level !== null && levelBlocked(level);
}

function isUhdCategory(category: Category): boolean {
  if (category.kind === 'live') return false;
  const level = uhdLevelOf(category.name);
  return level !== null && levelBlocked(level);
}

/**
 * The catalog with 4K/8K categories, search hits and "Recently added" titles left out while
 * `uhdHidden()`. Paged lists (`listItems`) stay whole: the grids place items by position, so a title
 * opened from there is stopped in the player instead (`blockedOnDevice`).
 */
export function withoutUnplayableUhd(catalog: CatalogApi): CatalogApi {
  const shown = (item: CatalogItem) => !blockedOnDevice(item);
  return {
    listSources: () => catalog.listSources(),
    addSource: (input, context) => catalog.addSource(input, context),
    refreshSource: (id, context) => catalog.refreshSource(id, context),
    reimportFile: (id, file, context) => catalog.reimportFile(id, file, context),
    removeSource: (id) => catalog.removeSource(id),
    listItems: (query) => catalog.listItems(query),
    getSeriesDetails: (sourceId, itemKey, context) => catalog.getSeriesDetails(sourceId, itemKey, context),
    async listCategories(sourceId: string, kind: ContentKind) {
      const list = await catalog.listCategories(sourceId, kind);
      return list.filter((category) => !isUhdCategory(category));
    },
    async listRecent(sourceId: string, kind: ContentKind, limit: number) {
      return (await catalog.listRecent(sourceId, kind, limit)).filter(shown);
    },
    async search(query) {
      return (await catalog.search(query)).filter((hit) => shown(hit.item));
    },
  };
}
